import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

const THEMES = {
  'professional-blue': {
    '--primary': '221 83% 40%',
    '--primary-foreground': '0 0% 100%',
    '--secondary': '210 100% 97%',
    '--accent': '199 89% 48%',
    '--background': '0 0% 100%',
    '--foreground': '222 47% 11%',
    '--card': '0 0% 100%',
    '--card-foreground': '222 47% 11%',
    '--popover': '0 0% 100%',
    '--popover-foreground': '222 47% 11%',
    '--muted': '210 40% 96%',
    '--muted-foreground': '215 16% 35%',
    '--border': '214 32% 91%',
    '--input': '214 32% 91%',
    '--ring': '221 83% 40%',
    '--destructive': '0 72% 51%',
    '--destructive-foreground': '0 0% 100%',
    '--sidebar-bg': '222 47% 6%',
    '--sidebar-fg': '214 32% 91%',
    '--sidebar-active': '221 83% 40%',
  },
  'ocean-teal': {
    '--primary': '187 85% 35%',
    '--primary-foreground': '0 0% 100%',
    '--secondary': '183 100% 96%',
    '--accent': '189 94% 43%',
    '--background': '0 0% 100%',
    '--foreground': '222 47% 11%',
    '--card': '0 0% 100%',
    '--card-foreground': '222 47% 11%',
    '--popover': '0 0% 100%',
    '--popover-foreground': '222 47% 11%',
    '--muted': '185 60% 96%',
    '--muted-foreground': '215 16% 35%',
    '--border': '214 32% 91%',
    '--input': '214 32% 91%',
    '--ring': '187 85% 35%',
    '--destructive': '0 72% 51%',
    '--destructive-foreground': '0 0% 100%',
    '--sidebar-bg': '197 71% 15%',
    '--sidebar-fg': '186 94% 90%',
    '--sidebar-active': '189 94% 43%',
  },
  'forest-green': {
    '--primary': '152 60% 36%',
    '--primary-foreground': '0 0% 100%',
    '--secondary': '138 76% 97%',
    '--accent': '142 71% 45%',
    '--background': '0 0% 100%',
    '--foreground': '222 47% 11%',
    '--card': '0 0% 100%',
    '--card-foreground': '222 47% 11%',
    '--popover': '0 0% 100%',
    '--popover-foreground': '222 47% 11%',
    '--muted': '138 76% 97%',
    '--muted-foreground': '215 16% 35%',
    '--border': '214 32% 91%',
    '--input': '214 32% 91%',
    '--ring': '152 60% 36%',
    '--destructive': '0 72% 51%',
    '--destructive-foreground': '0 0% 100%',
    '--sidebar-bg': '144 42% 14%',
    '--sidebar-fg': '141 79% 90%',
    '--sidebar-active': '142 71% 45%',
  },
  'royal-purple': {
    '--primary': '262 80% 50%',
    '--primary-foreground': '0 0% 100%',
    '--secondary': '251 100% 97%',
    '--accent': '258 90% 66%',
    '--background': '0 0% 100%',
    '--foreground': '222 47% 11%',
    '--card': '0 0% 100%',
    '--card-foreground': '222 47% 11%',
    '--popover': '0 0% 100%',
    '--popover-foreground': '222 47% 11%',
    '--muted': '251 100% 97%',
    '--muted-foreground': '215 16% 35%',
    '--border': '214 32% 91%',
    '--input': '214 32% 91%',
    '--ring': '262 80% 50%',
    '--destructive': '0 72% 51%',
    '--destructive-foreground': '0 0% 100%',
    '--sidebar-bg': '263 69% 21%',
    '--sidebar-fg': '250 100% 94%',
    '--sidebar-active': '258 90% 66%',
  },
  'sunset-orange': {
    '--primary': '24 95% 50%',
    '--primary-foreground': '0 0% 100%',
    '--secondary': '34 100% 96%',
    '--accent': '25 95% 53%',
    '--background': '0 0% 100%',
    '--foreground': '222 47% 11%',
    '--card': '0 0% 100%',
    '--card-foreground': '222 47% 11%',
    '--popover': '0 0% 100%',
    '--popover-foreground': '222 47% 11%',
    '--muted': '34 100% 96%',
    '--muted-foreground': '215 16% 35%',
    '--border': '214 32% 91%',
    '--input': '214 32% 91%',
    '--ring': '24 95% 50%',
    '--destructive': '0 72% 51%',
    '--destructive-foreground': '0 0% 100%',
    '--sidebar-bg': '22 85% 14%',
    '--sidebar-fg': '34 100% 91%',
    '--sidebar-active': '25 95% 53%',
  },
  'professional-slate': {
    '--primary': '215 25% 35%',
    '--primary-foreground': '0 0% 100%',
    '--secondary': '210 40% 96%',
    '--accent': '215 16% 47%',
    '--background': '0 0% 100%',
    '--foreground': '222 47% 11%',
    '--card': '0 0% 100%',
    '--card-foreground': '222 47% 11%',
    '--popover': '0 0% 100%',
    '--popover-foreground': '222 47% 11%',
    '--muted': '210 40% 96%',
    '--muted-foreground': '215 16% 35%',
    '--border': '214 32% 91%',
    '--input': '214 32% 91%',
    '--ring': '215 25% 35%',
    '--destructive': '0 72% 51%',
    '--destructive-foreground': '0 0% 100%',
    '--sidebar-bg': '217 33% 17%',
    '--sidebar-fg': '214 32% 91%',
    '--sidebar-active': '215 16% 47%',
  },
};

const ThemeContext = createContext(null);

export function useTheme() {
  return useContext(ThemeContext);
}

export function ThemeProvider({ children }) {
  const [themeId, setThemeId] = useState(() => {
    const saved = localStorage.getItem('tenant');
    if (saved) {
      try { return JSON.parse(saved).theme_id || 'professional-blue'; } catch { return 'professional-blue'; }
    }
    return 'professional-blue';
  });

  useEffect(() => {
    applyTheme(themeId);
  }, [themeId]);

  const applyTheme = (id) => {
    const vars = THEMES[id] || THEMES['professional-blue'];
    const root = document.documentElement;
    Object.entries(vars).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
  };

  const setTheme = (id) => {
    setThemeId(id);
    applyTheme(id);
  };

  const contextValue = useMemo(() => ({ themeId, setTheme, themes: THEMES }), [themeId]);

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

export { THEMES };

ThemeProvider.propTypes = {
  children: PropTypes.node,
};
